import React, { useEffect, useState } from 'react';
import { getToken } from '../../utils/getToken';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';

const baseURL = process.env.REACT_APP_BASE_URL || 'https://devapi.quickcare.ng/admin';

const AdminList = () => {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedAdmin, setSelectedAdmin] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        const fetchAdmins = async () => {
            const retrievedToken = getToken();
            if (!retrievedToken) {
                setError('No token found. Please login again.');
                setLoading(false);
                return;
            }

            const myHeaders = new Headers();
            myHeaders.append("Accept", "application/json");
            myHeaders.append("Authorization", `Bearer ${retrievedToken}`);

            const requestOptions = {
                method: "GET",
                headers: myHeaders,
                redirect: "follow"
            };

            try {
                const response = await fetch(`${baseURL}/admins`, requestOptions);
                const result = await response.json();

                if (response.ok) {
                    setAdmins(result.data);
                } else {
                    setError(result.message || 'Failed to fetch admins');
                }
            } catch (error) {
                console.error("Fetch error:", error);
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchAdmins();
    }, []);

    const handleModalOpen = (admin) => {
        setSelectedAdmin(admin);
        setIsModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedAdmin(null);
    };

    const handleDeleteAdmin = async (admin) => {
        const adminId = admin.id;
        const adminName = admin.full_name;

        if (window.confirm(`Are you sure you want to delete admin "${adminName}"?`)) {
            const token = getToken();
            const myHeaders = new Headers();
            myHeaders.append("Accept", "application/json");
            myHeaders.append("Authorization", `Bearer ${token}`);

            const requestOptions = {
                method: "DELETE",
                headers: myHeaders,
                redirect: "follow"
            };

            try {
                const response = await fetch(`${baseURL}/admins/${adminId}`, requestOptions);

                if (response.ok) {
                    setAdmins(prevAdmins => prevAdmins.filter(a => a.id !== adminId));
                    alert(`✅ Admin "${adminName}" has been successfully deleted.`);
                } else {
                    const result = await response.json();
                    setError(result.message || 'Failed to delete admin');
                }
            } catch (error) {
                console.error("Delete admin error:", error);
                setError(error.message);
            } finally {
                setIsModalOpen(false);
                setSelectedAdmin(null);
            }
        }
    };

    const handleChangeAdminRole = async (admin) => {
        const newRole = prompt(`Enter new role for ${admin.full_name}:`, admin.role);
        if (newRole && newRole !== admin.role) {
            const token = getToken();
            const myHeaders = new Headers();
            myHeaders.append("Content-Type", "application/json");
            myHeaders.append("Accept", "application/json");
            myHeaders.append("Authorization", `Bearer ${token}`);

            const raw = JSON.stringify({ role: newRole });

            const requestOptions = {
                method: "PATCH",
                headers: myHeaders,
                body: raw,
                redirect: "follow"
            };

            try {
                const response = await fetch(`${baseURL}/admins/${admin.id}/role`, requestOptions);
                if (response.ok) {
                    setAdmins(prevAdmins => 
                        prevAdmins.map(a => 
                            a.id === admin.id ? { ...a, role: newRole } : a
                        )
                    );
                    alert(`✅ Role updated to "${newRole}"`);
                } else {
                    const result = await response.json();
                    setError(result.message || 'Failed to update role');
                }
            } catch (error) {
                console.error("Update role error:", error);
                setError(error.message);
            }
        }
        setIsModalOpen(false);
        setSelectedAdmin(null);
    };

    // Get role badge color
    const getRoleColor = (role) => {
        return role === 'Super Admin' 
            ? 'bg-purple-100 text-purple-700 border-purple-200' 
            : 'bg-blue-100 text-blue-700 border-blue-200';
    };

    // Get initials from name
    const getInitials = (name) => {
        if (!name) return 'A';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    if (loading) {
        return (
            <div className="w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                <div className="flex flex-col items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#069125]"></div>
                    <p className="text-sm text-gray-500 mt-4">Loading admins...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                <div className="flex flex-col items-center justify-center py-8 text-center">
                    <span className="text-4xl mb-3">⚠️</span>
                    <h3 className="text-lg font-semibold text-gray-700">Error Loading Admins</h3>
                    <p className="text-sm text-gray-500 mt-1">{error}</p>
                    <button 
                        onClick={() => window.location.reload()}
                        className="mt-4 px-6 py-2 bg-[#069125] text-white text-sm font-medium rounded-xl hover:bg-[#057a1f] transition-colors"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 lg:p-6 border-b border-gray-100">
                <div className="flex items-center gap-3">
                    <div className="w-1 h-8 bg-gradient-to-b from-[#069125] to-[#057a1f] rounded-full"></div>
                    <div>
                        <h2 className="text-lg font-bold text-gray-800">Administrators</h2>
                        <p className="text-xs text-gray-400">
                            {admins.length} admin{admins.length !== 1 ? 's' : ''} registered
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2 mt-2 sm:mt-0">
                    <span className="text-xs text-gray-400 bg-gray-50 px-3 py-1 rounded-full">
                        👥 {admins.length} total
                    </span>
                </div>
            </div>

            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="bg-gradient-to-r from-gray-50 to-gray-100/80 border-b border-gray-200">
                            <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Admin</th>
                            <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Email</th>
                            <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Role</th>
                            <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Phone</th>
                            <th className="px-4 py-3.5 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {admins.length > 0 ? (
                            admins.map((admin, index) => (
                                <tr 
                                    key={admin.id || index}
                                    className={`border-b border-gray-50 hover:bg-green-50/30 transition-colors duration-150 ${
                                        index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                                    }`}
                                >
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#069125] to-[#057a1f] flex items-center justify-center text-white text-sm font-bold">
                                                {getInitials(admin.full_name)}
                                            </div>
                                            <span className="text-sm font-medium text-gray-800">
                                                {admin.full_name}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600">{admin.email}</td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold border ${getRoleColor(admin.role)}`}>
                                            {admin.role}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600">{admin.phone_number || '—'}</td>
                                    <td className="px-4 py-3 text-center">
                                        <button 
                                            onClick={() => handleModalOpen(admin)}
                                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                        >
                                            <MoreVertIcon className="text-gray-500" />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={5} className="px-4 py-12 text-center">
                                    <div className="flex flex-col items-center gap-2">
                                        <span className="text-3xl">📭</span>
                                        <span className="text-sm text-gray-500">No admins found</span>
                                        <span className="text-xs text-gray-400">Invite your first admin using the form above</span>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden divide-y divide-gray-100">
                {admins.length > 0 ? (
                    admins.map((admin, index) => (
                        <div key={admin.id || index} className="p-4 hover:bg-green-50/30 transition-colors">
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#069125] to-[#057a1f] flex items-center justify-center text-white text-lg font-bold">
                                        {getInitials(admin.full_name)}
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-800">{admin.full_name}</h3>
                                        <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${getRoleColor(admin.role)}`}>
                                            {admin.role}
                                        </span>
                                    </div>
                                </div>
                                <button 
                                    onClick={() => handleModalOpen(admin)}
                                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    <MoreVertIcon className="text-gray-500" />
                                </button>
                            </div>
                            <div className="grid grid-cols-2 gap-3 mt-2">
                                <div className="bg-gray-50 rounded-xl p-3">
                                    <p className="text-xs text-gray-500">Email</p>
                                    <p className="text-sm font-medium text-gray-700 truncate">{admin.email}</p>
                                </div>
                                <div className="bg-gray-50 rounded-xl p-3">
                                    <p className="text-xs text-gray-500">Phone</p>
                                    <p className="text-sm font-medium text-gray-700">{admin.phone_number || '—'}</p>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="p-8 text-center">
                        <span className="text-3xl block">📭</span>
                        <span className="text-sm text-gray-500 block mt-2">No admins found</span>
                    </div>
                )}
            </div>

            {/* Action Modal */}
            {isModalOpen && selectedAdmin && (
                <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
                    <div className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm" onClick={handleModalClose}></div>
                    <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm relative z-10 animate-slideUp">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#069125] to-[#057a1f] flex items-center justify-center text-white font-bold">
                                    {getInitials(selectedAdmin.full_name)}
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-800">{selectedAdmin.full_name}</h3>
                                    <p className="text-xs text-gray-400">{selectedAdmin.email}</p>
                                </div>
                            </div>
                            <button 
                                onClick={handleModalClose}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <CloseIcon className="text-gray-500" />
                            </button>
                        </div>

                        <div className="flex flex-col gap-2 mt-4">
                            <button 
                                onClick={() => handleChangeAdminRole(selectedAdmin)}
                                className="flex items-center gap-3 px-4 py-3 text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-200 group"
                            >
                                <div className="p-2 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
                                    <EditIcon className="text-blue-600" />
                                </div>
                                <span className="font-medium">Change Role</span>
                            </button>
                            <button 
                                onClick={() => handleDeleteAdmin(selectedAdmin)}
                                className="flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200 group"
                            >
                                <div className="p-2 bg-red-100 rounded-lg group-hover:bg-red-200 transition-colors">
                                    <DeleteIcon className="text-red-600" />
                                </div>
                                <span className="font-medium">Delete Admin</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminList;